import appStore from '../utils/state';
import { updateCart } from '../utils/updateCart';
import gsap from 'gsap';

/**
 * A single line-item card in the cart drawer list.
 *
 * Handles quantity +/- and remove interactions (optimistic UI, then a network
 * call to `/cart/change.js`) plus its own enter/exit animation. The instance
 * itself is short-lived: after a successful update, `cart-items.ts` replaces
 * the whole list with fresh server-rendered markup, so this element is torn
 * down and a new one takes its place. Because of that, there is no "confirm"
 * step here — only an optimistic update and a rollback path if the request
 * fails.
 */
export default class CartItem extends HTMLElement {
  public key: string = '';
  public quantity: number = 0;
  public isUpdating: boolean = false;
  public isRemoving: boolean = false;

  private committedQuantity: number = 0;

  private container: HTMLDivElement | null = null;
  private quantityLabel: HTMLSpanElement | null = null;
  private priceLabel: HTMLSpanElement | null = null;
  private compareAtPriceLabel: HTMLSpanElement | null = null;

  private decreaseButton: HTMLButtonElement | null = null;
  private increaseButton: HTMLButtonElement | null = null;
  private removeButton: HTMLButtonElement | null = null;

  private onDecreaseButtonClickBound = this.onDecreaseButtonClick.bind(this);
  private onIncreaseButtonClickBound = this.onIncreaseButtonClick.bind(this);
  private onRemoveButtonClickBound = this.onRemoveButtonClick.bind(this);

  private showTl: gsap.core.Timeline | null = null;
  private hideTl: gsap.core.Timeline | null = null;

  connectedCallback(): void {
    this.key = this.dataset.key ?? '';
    this.quantity = parseInt(this.dataset.quantity ?? '0', 10);
    this.committedQuantity = this.quantity;

    this.container = this.querySelector('[data-cart-item-ref="container"]');
    this.quantityLabel = this.querySelector('[data-cart-item-ref="quantity-label"]');
    this.priceLabel = this.querySelector('[data-cart-item-ref="price-label"]');
    this.compareAtPriceLabel = this.querySelector('[data-cart-item-ref="compare-at-price-label"]');

    this.decreaseButton = this.querySelector('[data-cart-item-ref="decrease-button"]');
    this.increaseButton = this.querySelector('[data-cart-item-ref="increase-button"]');
    this.removeButton = this.querySelector('[data-cart-item-ref="remove-button"]');

    if (this.dataset.freeGift === 'true') return;

    this.addEventListeners();
  }

  addEventListeners(): void {
    if (this.decreaseButton) {
      this.decreaseButton.addEventListener('click', this.onDecreaseButtonClickBound);
    }
    if (this.increaseButton) {
      this.increaseButton.addEventListener('click', this.onIncreaseButtonClickBound);
    }
    if (this.removeButton) {
      this.removeButton.addEventListener('click', this.onRemoveButtonClickBound);
    }
  }

  removeEventListeners(): void {
    if (this.decreaseButton) {
      this.decreaseButton.removeEventListener('click', this.onDecreaseButtonClickBound);
    }
    if (this.increaseButton) {
      this.increaseButton.removeEventListener('click', this.onIncreaseButtonClickBound);
    }
    if (this.removeButton) {
      this.removeButton.removeEventListener('click', this.onRemoveButtonClickBound);
    }
  }

  isBusy(): boolean {
    return appStore.getState().cartIsUpdating;
  }

  onDecreaseButtonClick() {
    if (this.dataset.freeGift === 'true') return;
    if (this.isBusy()) return;

    // Shopify's cart line `key` isn't guaranteed unique: a discount function that
    // discounts only part of a line's quantity (e.g. giftbox-loyalty-discount granting
    // 1 free unit out of 2) makes Shopify display that ONE underlying line as two
    // separate cart-item cards sharing the same key. `/cart/change.js` sets the
    // *total* quantity for a given key, so we must decrease the combined quantity of
    // all cards sharing this key, not just this card's own (partial) quantity.
    const totalQuantity = this.getTotalQuantityForKey();
    const nextTotalQuantity = Math.max(0, totalQuantity - 1);

    if (nextTotalQuantity === 0) {
      void this.applyRemove();
      return;
    }
    void this.applyQuantityUpdate(nextTotalQuantity);
  }

  onIncreaseButtonClick() {
    if (this.dataset.freeGift === 'true') return;
    if (this.isBusy()) return;

    // Same reasoning as onDecreaseButtonClick: increment the combined quantity of
    // every card sharing this key, not just this card's own (possibly partial) one.
    const totalQuantity = this.getTotalQuantityForKey();
    void this.applyQuantityUpdate(totalQuantity + 1);
  }

  onRemoveButtonClick() {
    if (this.dataset.freeGift === 'true') return;
    if (this.isBusy() || this.isRemoving) return;
    void this.applyRemove();
  }

  /**
   * Optimistically updates the label/price UI to `nextQuantity` before the
   * network round-trip resolves. On success, `updateCart` triggers a store
   * update that `cart-items.ts` uses to replace this element with a fresh one
   * from the server, so no further action is needed here. On failure, roll
   * back to `committedQuantity` — the quantity this element had when it was
   * last rendered from the server — since the element is never replaced.
   */
  private async applyQuantityUpdate(nextQuantity: number): Promise<void> {
    this.quantity = nextQuantity;
    this.isUpdating = true;
    this.updateQuantityLabel();
    this.setPriceLoading(true);

    const data = await updateCart(this.key, nextQuantity);

    if (!data) {
      this.revertQuantity(this.committedQuantity);
    }
  }

  private async applyRemove(): Promise<void> {
    this.hide();

    // Same reasoning as onDecreaseButtonClick: removing THIS card must only cancel
    // out its own share of the line's quantity. Sending quantity 0 unconditionally
    // would zero out every card sharing this key, including sibling giftbox cards
    // that are still supposed to remain in the cart.
    const totalQuantity = this.getTotalQuantityForKey();
    const remainingQuantity = Math.max(0, totalQuantity - this.quantity);

    const data = await updateCart(this.key, remainingQuantity);

    if (!data) {
      this.show();
    }
  }

  /**
   * Sums the quantity of every `<cart-item>` in the same list sharing this card's
   * `key` (including this one). Normally there's only one match (this card itself),
   * but a single Shopify cart line can render as multiple cards with an identical
   * key when a discount function targets only part of its quantity.
   *
   * Nested offered / threshold-gift cards (`data-free-gift` / `data-nested`) are
   * skipped: they are cart-transform components, not extra units of this line.
   * Counting them would send `quantity: 1` instead of `0` on parent remove.
   */
  private getTotalQuantityForKey(): number {
    const list = this.closest('[data-cart-items-ref="list"]');
    if (!list || !this.key) return this.quantity;

    let total = 0;
    for (const el of list.querySelectorAll('cart-item')) {
      const cartItem = el as CartItem;
      if (cartItem.dataset.freeGift === 'true' || cartItem.dataset.nested === 'true') continue;
      if (cartItem.key === this.key) {
        total += cartItem.quantity;
      }
    }
    return total;
  }

  public revertQuantity(qty: number): void {
    this.quantity = qty;
    this.dataset.quantity = qty.toString();
    this.isUpdating = false;
    this.updateQuantityLabel();
    this.setPriceLoading(false);
  }

  private setPriceLoading(isLoading: boolean): void {
    if (this.priceLabel) {
      this.priceLabel.classList.toggle('shimmer-text', isLoading);
    }

    if (this.compareAtPriceLabel && !this.compareAtPriceLabel.classList.contains('hidden')) {
      this.compareAtPriceLabel.classList.toggle('shimmer-text', isLoading);
    }
  }

  updateQuantityLabel(): void {
    if (this.quantityLabel) {
      this.quantityLabel.textContent = this.quantity.toString();
    }
  }

  /** Slides the card out, fades it, then collapses its height to zero. */
  public hide(onComplete?: () => void): void {
    this.isRemoving = true;

    if (this.hideTl) {
      this.hideTl.kill();
    }

    this.hideTl = gsap.timeline({
      onComplete: () => {
        this.isRemoving = false;
        this.hideTl = null;
        onComplete?.();
      },
    });

    this.hideTl.to(this.container, { x: 40, duration: 0.7, ease: 'expo.out' }, 0);
    this.hideTl.to(this.container, { autoAlpha: 0, duration: 0.2, ease: 'power2.out' }, 0.1);
    this.hideTl.to(this, { height: 0, duration: 0.7, ease: 'expo.out' }, 0.3);
  }

  /**
   * Same intent as `hide`, but safe to call while an exit animation started
   * by `hideAsync` is already running: instead of starting a second
   * competing timeline, it just chains `onComplete` onto the in-flight one.
   */
  public confirmRemove(onComplete?: () => void): void {
    if (this.isRemoving && this.hideTl) {
      this.hideTl.eventCallback('onComplete', () => {
        this.isRemoving = false;
        this.hideTl = null;
        onComplete?.();
      });
      return;
    }

    this.hide(onComplete);
  }

  /** Promise wrapper around hide/confirmRemove for awaitable exit animations. */
  public hideAsync(): Promise<void> {
    return new Promise((resolve) => {
      if (this.isRemoving) {
        this.confirmRemove(() => resolve());
        return;
      }
      this.hide(() => resolve());
    });
  }

  /** Reverse of `hide`: expands the height, then slides/fades the card in. */
  public show(): void {
    void this.showAsync();
  }

  /** Promise wrapper around show for awaitable enter animations. */
  public showAsync(): Promise<void> {
    return new Promise((resolve) => {
      if (this.hideTl) {
        this.hideTl.kill();
        this.hideTl = null;
      }
      if (this.showTl) {
        this.showTl.kill();
        this.showTl = null;
      }

      const height = this.container?.offsetHeight ?? 0;

      this.isRemoving = false;

      this.showTl = gsap.timeline({
        onComplete: () => {
          this.showTl = null;
          resolve();
        },
      });

      this.showTl.fromTo(this, { height: 0 }, { height: height, duration: 0.7, ease: 'expo.out' }, 0);
      this.showTl.fromTo(this.container, { x: 20 }, { x: 0, duration: 0.7, ease: 'expo.out' }, 0.3);
      this.showTl.fromTo(this.container, { autoAlpha: 0 }, { duration: 0.2, autoAlpha: 1, ease: 'power2.out' }, 0.3);
    });
  }

  disconnectedCallback(): void {
    this.removeEventListeners();
  }
}

customElements.define('cart-item', CartItem);
